// ./react-app/src/components/vnr/RoiPerMonth.tsx
import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../../themes/chartTheme';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import LoadingSpinner from '../LoadingSpinner';
import moment from 'moment';

interface IRoiRatePerMonth {
  leads: number;
  revenue_30d: number;
  revenue_total: number;
  total_cost: number;
  roi30: number;
  roi_total: number;
  month: number;
  year: number;
}

interface ISerie {
  id: string;
  data: { x: string; y: number }[];
  yAxisId?: string;
}

const RoiPerMonth: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<ISerie[]>([]);
  const [originalData, setOriginalData] = useState<IRoiRatePerMonth[]>([]);
  const [loading, setLoading] = useState(false);
  const determineAggregation = (
    startDate: Date,
    endDate: Date,
    userSelection: string,
  ): 'daily' | 'weekly' | 'monthly' => {
    if (userSelection !== 'auto') {
      return userSelection as 'daily' | 'weekly' | 'monthly';
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 120) return 'monthly';
    if (diffDays > 45) return 'weekly';
    return 'daily';
  };

  const aggregateTimelineDataForRoi = (
    data: any[],
    aggregationType: 'daily' | 'weekly' | 'monthly',
  ): any[] => {
    if (aggregationType === 'daily') return data;

    const aggregated: { [key: string]: any } = {};

    data.forEach((item) => {
      let key: string;
      const date = moment(item.date);

      if (aggregationType === 'weekly') {
        key = `${date.year()}-W${date.isoWeek().toString().padStart(2, '0')}`;
      } else {
        key = date.format('YYYY-MM');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          revenue_30d: 0,
          revenue_total: 0,
          leads: 0,
          total_cost: 0,
        };
      }

      aggregated[key].revenue_30d += parseFloat(String(item.revenue_30d)) || 0;
      aggregated[key].revenue_total += parseFloat(String(item.revenue_total)) || 0;
      aggregated[key].leads += parseInt(String(item.leads)) || 0;
      aggregated[key].total_cost += parseFloat(String(item.total_cost)) || 0;
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  };

  useEffect(() => {
    const getData = async (filterState: IFilterState) => {
      // Use current filter dates or fallback to 1 year range
      const chartStartDate = filterState.startDate || moment().subtract(1, 'year').toDate();
      const chartEndDate = filterState.endDate || moment().toDate();

      const networks = Array.isArray(filterState.network)
        ? filterState.network
        : [filterState.network];
      const themes = Array.isArray(filterState.theme)
        ? filterState.theme
        : filterState.theme
          ? [filterState.theme]
          : [];
      const options = {
        start_date: chartStartDate.toISOString().split('T')[0],
        end_date: chartEndDate.toISOString().split('T')[0],
        network: networks,
        ...(themes.length > 0 && { theme: themes }),
        ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
      };

      // Use the more flexible revenue-timeline endpoint with ROI calculation
      const rawData = await fetchData<any[]>('/api/vnr/roi-per-month', options, true);

      return rawData;
    };

    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork) {
        return;
      }

      setLoading(true);
      try {
        const rawData = await getData(filterState);

        // For ROI per month, we'll still use the monthly-aggregated backend data
        // but format it according to user's aggregation preference for display
        const data = rawData as IRoiRatePerMonth[];

        // Find max leads to normalize the scale
        const maxLeads = Math.max(...data.map((d) => parseInt(String(d.leads)) || 0));
        const maxRoi = Math.max(
          ...data.map((d) => {
            const revenue30d = parseFloat(String(d.revenue_30d)) || 0;
            const revenueTotal = parseFloat(String(d.revenue_total)) || 0;
            const totalCost = parseFloat(String(d.total_cost)) || 0;
            const roi30 = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
            const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;
            return Math.max(roi30, roiTotal);
          }),
        );

        const processedData: ISerie[] = [
          {
            id: 'ROI(30)',
            yAxisId: 'left',
            data: data.map((d) => {
              const revenue30d = parseFloat(String(d.revenue_30d)) || 0;
              const totalCost = parseFloat(String(d.total_cost)) || 0;
              const roi30 = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
              return {
                x: `${d.month}-${d.year}`,
                y: roi30,
              };
            }),
          },
          {
            id: 'ROI Laufend',
            yAxisId: 'left',
            data: data.map((d) => {
              const revenueTotal = parseFloat(String(d.revenue_total)) || 0;
              const totalCost = parseFloat(String(d.total_cost)) || 0;
              const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;
              return {
                x: `${d.month}-${d.year}`,
                y: roiTotal,
              };
            }),
          },
          {
            id: 'Leads',
            yAxisId: 'right',
            data: data.map((d) => {
              const leads = parseInt(String(d.leads)) || 0;
              // Normalize leads to fit roughly the same scale as ROI
              const normalizedLeads = maxLeads > 0 ? (leads / maxLeads) * maxRoi : 0;
              return {
                x: `${d.month}-${d.year}`,
                y: normalizedLeads,
              };
            }),
          },
        ];
        setChartData(processedData);
        setOriginalData(data);
      } catch (error) {
        console.error('Error fetching and processing data: ', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.vnrAggregation,
    filterState.startDate,
    filterState.endDate,
  ]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (chartData.length === 0) {
    return (
      <Box sx={{ margin: 2, textAlign: 'center', color: 'text.secondary' }}>
        <p>No ROI data available for the selected period and filters.</p>
      </Box>
    );
  }

  return (
    <>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
        axisTop={null}
        axisRight={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Leads',
          legendOffset: 40,
          legendPosition: 'middle',
          format: (value) => {
            if (originalData.length === 0) return value;
            // Find max leads and ROI to reverse the normalization
            const maxLeads = Math.max(...originalData.map((d) => parseInt(String(d.leads)) || 0));
            const maxRoi = Math.max(
              ...originalData.map((d) => {
                const revenue30d = parseFloat(String(d.revenue_30d)) || 0;
                const revenueTotal = parseFloat(String(d.revenue_total)) || 0;
                const totalCost = parseFloat(String(d.total_cost)) || 0;
                const roi30 = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
                const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;
                return Math.max(roi30, roiTotal);
              }),
            );
            // Convert normalized value back to actual leads count
            const actualLeads = maxRoi > 0 ? Math.round((value / maxRoi) * maxLeads) : 0;
            return actualLeads.toString();
          },
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Datum',
          legendPosition: 'middle',
          legendOffset: 32,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'ROI Rate (%)',
          legendOffset: -40,
          legendPosition: 'middle',
        }}
        pointSize={8}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        useMesh={true}
        theme={chartTheme}
        lineWidth={4}
        enableGridX={false}
        enableGridY={false}
        tooltip={({ point }) => {
          const value = parseFloat(point.data.yFormatted as string);
          let displayValue;
          let unit;

          if (point.serieId === 'Leads') {
            // Convert normalized value back to actual leads count
            if (originalData.length === 0) {
              displayValue = value.toFixed(0);
              unit = '';
            } else {
              const maxLeads = Math.max(...originalData.map((d) => parseInt(String(d.leads)) || 0));
              const maxRoi = Math.max(
                ...originalData.map((d) => {
                  const revenue30d = parseFloat(String(d.revenue_30d)) || 0;
                  const revenueTotal = parseFloat(String(d.revenue_total)) || 0;
                  const totalCost = parseFloat(String(d.total_cost)) || 0;
                  const roi30 = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
                  const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;
                  return Math.max(roi30, roiTotal);
                }),
              );
              const actualLeads = maxRoi > 0 ? Math.round((value / maxRoi) * maxLeads) : 0;
              displayValue = actualLeads.toFixed(0);
              unit = '';
            }
          } else {
            displayValue = value.toFixed(2);
            unit = ' %';
          }

          return (
            <Box
              sx={{
                padding: '5px 10px',
                color: '#222',
                background: '#e0e0e0',
                boxShadow: '0 0 2px #222',
              }}
            >
              <strong>{point.serieId}</strong>: {displayValue}
              {unit}
            </Box>
          );
        }}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .03)',
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </>
  );
};

export default RoiPerMonth;
